import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const OverlayEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isEdit = Boolean(id)
  
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    missionName: '',
    rocket: '',
    provider: '',
    facility: '',
    launchPad: '',
    timezone: 'UTC',
    t0Timestamp: '',
    windowOpen: '',
    windowClose: '',
    updateType: 'LATEST_UPDATE',
    headline1: '',
    subHeadline: '',
    headline3: '',
    events: [],
    statusVisible: true
  })

  useEffect(() => {
    if (isEdit) {
      fetchOverlay()
    }
  }, [id, isEdit])

  const fetchOverlay = async () => {
    try {
      const response = await api.get(`/overlays/${id}`)
      const { overlay, events } = response.data
      
      
      setFormData({
        title: overlay.title || '',
        subtitle: overlay.subtitle || '',
        missionName: overlay.missionName || '',
        rocket: overlay.rocket || '',
        provider: overlay.provider || '',
        facility: overlay.facility || '',
        launchPad: overlay.launchPad || '',
        timezone: overlay.timezone || 'UTC',
        t0Timestamp: overlay.t0Timestamp ? new Date(overlay.t0Timestamp).toISOString().slice(0, 19) : '',
        windowOpen: overlay.windowOpen ? new Date(overlay.windowOpen).toISOString().slice(0, 19) : '',
        windowClose: overlay.windowClose ? new Date(overlay.windowClose).toISOString().slice(0, 19) : '',
        updateType: overlay.updateType || 'LATEST_UPDATE',
        headline1: overlay.headline1 || '',
        subHeadline: overlay.subHeadline || '',
        headline3: overlay.headline3 || '',
        events: events || [],
        statusVisible: overlay.statusVisible !== undefined ? overlay.statusVisible : true
      })
    } catch (error) {
      console.error('Failed to fetch overlay:', error)
      toast.error('Failed to load overlay')
      navigate('/overlay')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleEventChange = (eventType, index, field, value) => {
    setFormData(prev => {
      const newEvents = [...prev.events]
      const filteredEvents = newEvents.filter(event => event.eventType === eventType)
      const otherEvents = newEvents.filter(event => event.eventType !== eventType)
      
      if (filteredEvents[index]) {
        filteredEvents[index] = { ...filteredEvents[index], [field]: value }
      }
      
      return {
        ...prev,
        events: [...otherEvents, ...filteredEvents]
      }
    })
  }

  const addEvent = (eventType) => {
    const newEvent = {
      eventType: eventType,
      eventName: '',
      timeOffset: 0,
      displayOrientation: 'VERTICAL'
    }
    
    setFormData(prev => ({
      ...prev,
      events: [...prev.events, newEvent]
    }))
  }

  const removeEvent = (eventType, index) => {
    setFormData(prev => {
      const newEvents = [...prev.events]
      const filteredEvents = newEvents.filter(event => event.eventType === eventType)
      const otherEvents = newEvents.filter(event => event.eventType !== eventType)
      
      const updatedFilteredEvents = filteredEvents.filter((_, i) => i !== index)
      
      return {
        ...prev,
        events: [...otherEvents, ...updatedFilteredEvents]
      }
    })
  }

  const toggleEventOrientation = (eventType, orientation) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.map(event => 
        event.eventType === eventType 
          ? { ...event, displayOrientation: orientation }
          : event
      )
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      const payload = {
        ...formData,
        t0Timestamp: formData.t0Timestamp ? new Date(formData.t0Timestamp).toISOString() : null,
        windowOpen: formData.windowOpen ? new Date(formData.windowOpen).toISOString() : null,
        windowClose: formData.windowClose ? new Date(formData.windowClose).toISOString() : null,
        // Explicitly include headline3 to ensure it's always sent
        headline3: formData.headline3 !== undefined ? formData.headline3 : '',
        events: formData.events.map((event, index) => ({
          ...event,
          sortOrder: index
        }))
      }
      
      console.log('=== FRONTEND SAVE PAYLOAD ===');
      console.log('headline3 in formData:', formData.headline3);
      console.log('headline3 type in formData:', typeof formData.headline3);
      console.log('headline3 in payload:', payload.headline3);
      console.log('headline3 type in payload:', typeof payload.headline3);
      console.log('Full payload keys:', Object.keys(payload));
      console.log('Payload headline3 check:', 'headline3' in payload);

      if (isEdit) {
        const response = await api.put(`/overlays/${id}`, payload)
        console.log('Save response:', response.data);
        toast.success('Overlay updated successfully')
      } else {
        const response = await api.post('/overlays', payload)
        toast.success('Overlay created successfully')
        navigate(`/overlays/${response.data.overlay.id}/edit`)
      }
    } catch (error) {
      console.error('Failed to save overlay:', error)
      toast.error('Failed to save overlay')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white">Loading overlay...</p>
        </div>
      </div>
    )
  }

  // Get all flight events for rendering
  const flightEvents = formData.events.filter(event => event.eventType === 'FLIGHT_EVENT')
  const flightOrientation = flightEvents.length > 0 ? flightEvents[0].displayOrientation : 'VERTICAL'

  return (
    <div className="bg-[#1c1c1c] text-white p-8">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/overlay')}
          className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir"
        >
          <span>&lt;</span>
          BACK
        </button>
      </div>

      {/* Title and Subtitle Section */}
      <div className="mb-8">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Overlay Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Enter overlay title"
            />
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">Overlay Subtitle</label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Enter overlay subtitle"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* First Row - Mission Name and T-0 */}
        <div className="grid grid-cols-2 gap-8">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Mission Name</label>
            <input
              type="text"
              name="missionName"
              value={formData.missionName}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">T-0</label>
            <input
              type="datetime-local"
              name="t0Timestamp"
              value={formData.t0Timestamp}
              onChange={handleChange}
              step="1"
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
            />
          </div>
        </div>

        {/* Second Row - Window Open, Window Close */}
        <div className="grid grid-cols-2 gap-6">
          {/* Hidden: Rocket */}
          {/* <div>
            <label className="block text-white text-sm font-medium mb-2">Rocket</label>
            <input
              type="text"
              name="rocket"
              value={formData.rocket}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div> */}
          {/* Hidden: Provider */}
          {/* <div>
            <label className="block text-white text-sm font-medium mb-2">Provider</label>
            <input
              type="text"
              name="provider"
              value={formData.provider}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div> */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">Window Open</label>
            <input
              type="datetime-local"
              name="windowOpen"
              value={formData.windowOpen}
              onChange={handleChange}
              step="1"
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">Window Close</label>
            <input
              type="datetime-local"
              name="windowClose"
              value={formData.windowClose}
              onChange={handleChange}
              step="1"
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
            />
          </div>
        </div>

        {/* Third Row - Facility and Time Zone */}
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Facility</label>
            <input
              type="text"
              name="facility"
              value={formData.facility}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div>
          {/* Hidden: Launch Pad */}
          {/* <div>
            <label className="block text-white text-sm font-medium mb-2">Launch Pad</label>
            <input
              type="text"
              name="launchPad"
              value={formData.launchPad}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div> */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">Time Zone</label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
            >
              <option value="UTC">UTC - Coordinated Universal Time</option>
              <option value="America/New_York">America/New_York - Eastern Time (ET)</option>
              <option value="America/Chicago">America/Chicago - Central Time (CT)</option>
              <option value="America/Denver">America/Denver - Mountain Time (MT)</option>
              <option value="America/Los_Angeles">America/Los_Angeles - Pacific Time (PT)</option>
              <option value="America/Anchorage">America/Anchorage - Alaska Time (AT)</option>
              <option value="Pacific/Honolulu">Pacific/Honolulu - Hawaii Time (HT)</option>
              <option value="Europe/London">Europe/London - Greenwich Mean Time (GMT)</option>
              <option value="Europe/Paris">Europe/Paris - Central European Time (CET)</option>
              <option value="Europe/Berlin">Europe/Berlin - Central European Time (CET)</option>
              <option value="Europe/Rome">Europe/Rome - Central European Time (CET)</option>
              <option value="Europe/Madrid">Europe/Madrid - Central European Time (CET)</option>
              <option value="Europe/Amsterdam">Europe/Amsterdam - Central European Time (CET)</option>
              <option value="Europe/Brussels">Europe/Brussels - Central European Time (CET)</option>
              <option value="Europe/Vienna">Europe/Vienna - Central European Time (CET)</option>
              <option value="Europe/Zurich">Europe/Zurich - Central European Time (CET)</option>
              <option value="Europe/Stockholm">Europe/Stockholm - Central European Time (CET)</option>
              <option value="Europe/Oslo">Europe/Oslo - Central European Time (CET)</option>
              <option value="Europe/Copenhagen">Europe/Copenhagen - Central European Time (CET)</option>
              <option value="Europe/Helsinki">Europe/Helsinki - Eastern European Time (EET)</option>
              <option value="Europe/Athens">Europe/Athens - Eastern European Time (EET)</option>
              <option value="Europe/Istanbul">Europe/Istanbul - Turkey Time (TRT)</option>
              <option value="Europe/Moscow">Europe/Moscow - Moscow Time (MSK)</option>
              <option value="Asia/Tokyo">Asia/Tokyo - Japan Standard Time (JST)</option>
              <option value="Asia/Shanghai">Asia/Shanghai - China Standard Time (CST)</option>
              <option value="Asia/Hong_Kong">Asia/Hong_Kong - Hong Kong Time (HKT)</option>
              <option value="Asia/Singapore">Asia/Singapore - Singapore Time (SGT)</option>
              <option value="Asia/Seoul">Asia/Seoul - Korea Standard Time (KST)</option>
              <option value="Asia/Taipei">Asia/Taipei - Taiwan Time (CST)</option>
              <option value="Asia/Bangkok">Asia/Bangkok - Indochina Time (ICT)</option>
              <option value="Asia/Jakarta">Asia/Jakarta - Western Indonesia Time (WIB)</option>
              <option value="Asia/Manila">Asia/Manila - Philippine Time (PHT)</option>
              <option value="Asia/Kolkata">Asia/Kolkata - India Standard Time (IST)</option>
              <option value="Asia/Dubai">Asia/Dubai - Gulf Standard Time (GST)</option>
              <option value="Asia/Tehran">Asia/Tehran - Iran Standard Time (IRST)</option>
              <option value="Asia/Karachi">Asia/Karachi - Pakistan Standard Time (PKT)</option>
              <option value="Asia/Dhaka">Asia/Dhaka - Bangladesh Standard Time (BST)</option>
              <option value="Asia/Kathmandu">Asia/Kathmandu - Nepal Time (NPT)</option>
              <option value="Asia/Colombo">Asia/Colombo - Sri Lanka Time (SLST)</option>
              <option value="Asia/Kabul">Asia/Kabul - Afghanistan Time (AFT)</option>
              <option value="Asia/Tashkent">Asia/Tashkent - Uzbekistan Time (UZT)</option>
              <option value="Asia/Almaty">Asia/Almaty - Alma-Ata Time (ALMT)</option>
              <option value="Asia/Yekaterinburg">Asia/Yekaterinburg - Yekaterinburg Time (YEKT)</option>
              <option value="Asia/Novosibirsk">Asia/Novosibirsk - Novosibirsk Time (NOVT)</option>
              <option value="Asia/Krasnoyarsk">Asia/Krasnoyarsk - Krasnoyarsk Time (KRAT)</option>
              <option value="Asia/Irkutsk">Asia/Irkutsk - Irkutsk Time (IRKT)</option>
              <option value="Asia/Yakutsk">Asia/Yakutsk - Yakutsk Time (YAKT)</option>
              <option value="Asia/Vladivostok">Asia/Vladivostok - Vladivostok Time (VLAT)</option>
              <option value="Asia/Magadan">Asia/Magadan - Magadan Time (MAGT)</option>
              <option value="Asia/Kamchatka">Asia/Kamchatka - Kamchatka Time (PETT)</option>
              <option value="Australia/Sydney">Australia/Sydney - Australian Eastern Time (AET)</option>
              <option value="Australia/Melbourne">Australia/Melbourne - Australian Eastern Time (AET)</option>
              <option value="Australia/Brisbane">Australia/Brisbane - Australian Eastern Time (AET)</option>
              <option value="Australia/Perth">Australia/Perth - Australian Western Time (AWT)</option>
              <option value="Australia/Adelaide">Australia/Adelaide - Australian Central Time (ACT)</option>
              <option value="Australia/Darwin">Australia/Darwin - Australian Central Time (ACT)</option>
              <option value="Pacific/Auckland">Pacific/Auckland - New Zealand Time (NZST)</option>
              <option value="Pacific/Fiji">Pacific/Fiji - Fiji Time (FJT)</option>
              <option value="Pacific/Tahiti">Pacific/Tahiti - Tahiti Time (TAHT)</option>
              <option value="Pacific/Guam">Pacific/Guam - Chamorro Time (ChST)</option>
              <option value="Pacific/Saipan">Pacific/Saipan - Chamorro Time (ChST)</option>
              <option value="Pacific/Port_Moresby">Pacific/Port_Moresby - Papua New Guinea Time (PGT)</option>
              <option value="Pacific/Noumea">Pacific/Noumea - New Caledonia Time (NCT)</option>
              <option value="Pacific/Vanuatu">Pacific/Vanuatu - Vanuatu Time (VUT)</option>
              <option value="Pacific/Solomon">Pacific/Solomon - Solomon Islands Time (SBT)</option>
              <option value="Pacific/Norfolk">Pacific/Norfolk - Norfolk Time (NFT)</option>
              <option value="Pacific/Pitcairn">Pacific/Pitcairn - Pitcairn Time (PST)</option>
              <option value="Pacific/Easter">Pacific/Easter - Easter Island Time (EAST)</option>
              <option value="Pacific/Galapagos">Pacific/Galapagos - Galapagos Time (GALT)</option>
              <option value="Pacific/Marquesas">Pacific/Marquesas - Marquesas Time (MART)</option>
              <option value="Pacific/Gambier">Pacific/Gambier - Gambier Time (GAMT)</option>
              <option value="Pacific/Chatham">Pacific/Chatham - Chatham Time (CHAST)</option>
              <option value="Atlantic/Azores">Atlantic/Azores - Azores Time (AZOT)</option>
              <option value="Atlantic/Canary">Atlantic/Canary - Western European Time (WET)</option>
              <option value="Atlantic/Madeira">Atlantic/Madeira - Western European Time (WET)</option>
              <option value="Atlantic/Cape_Verde">Atlantic/Cape_Verde - Cape Verde Time (CVT)</option>
              <option value="Atlantic/South_Georgia">Atlantic/South_Georgia - South Georgia Time (GST)</option>
              <option value="Atlantic/Stanley">Atlantic/Stanley - Falkland Islands Time (FKT)</option>
              <option value="Atlantic/Bermuda">Atlantic/Bermuda - Atlantic Time (AT)</option>
              <option value="Atlantic/St_Helena">Atlantic/St_Helena - Greenwich Mean Time (GMT)</option>
              <option value="Atlantic/Ascension">Atlantic/Ascension - Greenwich Mean Time (GMT)</option>
              <option value="Atlantic/Tristan_da_Cunha">Atlantic/Tristan_da_Cunha - Saint Helena Time (SHET)</option>
              <option value="Indian/Maldives">Indian/Maldives - Maldives Time (MVT)</option>
              <option value="Indian/Mauritius">Indian/Mauritius - Mauritius Time (MUT)</option>
              <option value="Indian/Reunion">Indian/Reunion - Reunion Time (RET)</option>
              <option value="Indian/Seychelles">Indian/Seychelles - Seychelles Time (SCT)</option>
              <option value="Indian/Comoro">Indian/Comoro - East Africa Time (EAT)</option>
              <option value="Indian/Mayotte">Indian/Mayotte - East Africa Time (EAT)</option>
              <option value="Indian/Antananarivo">Indian/Antananarivo - East Africa Time (EAT)</option>
              <option value="Indian/Mahe">Indian/Mahe - Seychelles Time (SCT)</option>
              <option value="Indian/Kerguelen">Indian/Kerguelen - French Southern Time (TFT)</option>
              <option value="Indian/Chagos">Indian/Chagos - Indian Ocean Time (IOT)</option>
              <option value="Indian/Cocos">Indian/Cocos - Cocos Islands Time (CCT)</option>
              <option value="Indian/Christmas">Indian/Christmas - Christmas Island Time (CXT)</option>
              <option value="Africa/Cairo">Africa/Cairo - Eastern European Time (EET)</option>
              <option value="Africa/Johannesburg">Africa/Johannesburg - South Africa Time (SAST)</option>
              <option value="Africa/Lagos">Africa/Lagos - West Africa Time (WAT)</option>
              <option value="Africa/Casablanca">Africa/Casablanca - Western European Time (WET)</option>
              <option value="Africa/Algiers">Africa/Algiers - Central European Time (CET)</option>
              <option value="Africa/Tunis">Africa/Tunis - Central European Time (CET)</option>
              <option value="Africa/Tripoli">Africa/Tripoli - Eastern European Time (EET)</option>
              <option value="Africa/Nairobi">Africa/Nairobi - East Africa Time (EAT)</option>
              <option value="Africa/Addis_Ababa">Africa/Addis_Ababa - East Africa Time (EAT)</option>
              <option value="Africa/Dar_es_Salaam">Africa/Dar_es_Salaam - East Africa Time (EAT)</option>
              <option value="Africa/Kampala">Africa/Kampala - East Africa Time (EAT)</option>
              <option value="Africa/Khartoum">Africa/Khartoum - Central Africa Time (CAT)</option>
              <option value="Africa/Juba">Africa/Juba - East Africa Time (EAT)</option>
              <option value="Africa/Djibouti">Africa/Djibouti - East Africa Time (EAT)</option>
              <option value="Africa/Asmara">Africa/Asmara - East Africa Time (EAT)</option>
              <option value="Africa/Mogadishu">Africa/Mogadishu - East Africa Time (EAT)</option>
              <option value="Africa/Maputo">Africa/Maputo - Central Africa Time (CAT)</option>
              <option value="Africa/Lusaka">Africa/Lusaka - Central Africa Time (CAT)</option>
              <option value="Africa/Harare">Africa/Harare - Central Africa Time (CAT)</option>
              <option value="Africa/Gaborone">Africa/Gaborone - Central Africa Time (CAT)</option>
              <option value="Africa/Windhoek">Africa/Windhoek - Central Africa Time (CAT)</option>
              <option value="Africa/Luanda">Africa/Luanda - West Africa Time (WAT)</option>
              <option value="Africa/Kinshasa">Africa/Kinshasa - West Africa Time (WAT)</option>
              <option value="Africa/Brazzaville">Africa/Brazzaville - West Africa Time (WAT)</option>
              <option value="Africa/Douala">Africa/Douala - West Africa Time (WAT)</option>
              <option value="Africa/Libreville">Africa/Libreville - West Africa Time (WAT)</option>
              <option value="Africa/Malabo">Africa/Malabo - West Africa Time (WAT)</option>
              <option value="Africa/Ndjamena">Africa/Ndjamena - West Africa Time (WAT)</option>
              <option value="Africa/Bangui">Africa/Bangui - West Africa Time (WAT)</option>
              <option value="Africa/Bujumbura">Africa/Bujumbura - Central Africa Time (CAT)</option>
              <option value="Africa/Kigali">Africa/Kigali - Central Africa Time (CAT)</option>
              <option value="Africa/Bujumbura">Africa/Bujumbura - Central Africa Time (CAT)</option>
              <option value="Africa/Blantyre">Africa/Blantyre - Central Africa Time (CAT)</option>
              <option value="Africa/Lilongwe">Africa/Lilongwe - Central Africa Time (CAT)</option>
              <option value="Africa/Maseru">Africa/Maseru - South Africa Time (SAST)</option>
              <option value="Africa/Mbabane">Africa/Mbabane - South Africa Time (SAST)</option>
              <option value="Africa/Lesotho">Africa/Lesotho - South Africa Time (SAST)</option>
              <option value="Africa/Swaziland">Africa/Swaziland - South Africa Time (SAST)</option>
              <option value="Africa/Porto-Novo">Africa/Porto-Novo - West Africa Time (WAT)</option>
              <option value="Africa/Ouagadougou">Africa/Ouagadougou - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Abidjan">Africa/Abidjan - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Accra">Africa/Accra - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Bamako">Africa/Bamako - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Banjul">Africa/Banjul - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Bissau">Africa/Bissau - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Conakry">Africa/Conakry - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Dakar">Africa/Dakar - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Freetown">Africa/Freetown - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Lome">Africa/Lome - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Monrovia">Africa/Monrovia - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Nouakchott">Africa/Nouakchott - Greenwich Mean Time (GMT)</option>
              <option value="Africa/Sao_Tome">Africa/Sao_Tome - Greenwich Mean Time (GMT)</option>
              <option value="America/Argentina/Buenos_Aires">America/Argentina/Buenos_Aires - Argentina Time (ART)</option>
              <option value="America/Argentina/Cordoba">America/Argentina/Cordoba - Argentina Time (ART)</option>
              <option value="America/Argentina/Mendoza">America/Argentina/Mendoza - Argentina Time (ART)</option>
              <option value="America/Argentina/Salta">America/Argentina/Salta - Argentina Time (ART)</option>
              <option value="America/Argentina/Tucuman">America/Argentina/Tucuman - Argentina Time (ART)</option>
              <option value="America/Argentina/Ushuaia">America/Argentina/Ushuaia - Argentina Time (ART)</option>
              <option value="America/Bahia">America/Bahia - Brasilia Time (BRT)</option>
              <option value="America/Belem">America/Belem - Brasilia Time (BRT)</option>
              <option value="America/Fortaleza">America/Fortaleza - Brasilia Time (BRT)</option>
              <option value="America/Maceio">America/Maceio - Brasilia Time (BRT)</option>
              <option value="America/Recife">America/Recife - Brasilia Time (BRT)</option>
              <option value="America/Salvador">America/Salvador - Brasilia Time (BRT)</option>
              <option value="America/Sao_Paulo">America/Sao_Paulo - Brasilia Time (BRT)</option>
              <option value="America/Araguaina">America/Araguaina - Brasilia Time (BRT)</option>
              <option value="America/Cuiaba">America/Cuiaba - Amazon Time (AMT)</option>
              <option value="America/Campo_Grande">America/Campo_Grande - Amazon Time (AMT)</option>
              <option value="America/Manaus">America/Manaus - Amazon Time (AMT)</option>
              <option value="America/Porto_Velho">America/Porto_Velho - Amazon Time (AMT)</option>
              <option value="America/Boa_Vista">America/Boa_Vista - Amazon Time (AMT)</option>
              <option value="America/Rio_Branco">America/Rio_Branco - Acre Time (ACT)</option>
              <option value="America/Eirunepe">America/Eirunepe - Acre Time (ACT)</option>
              <option value="America/Santiago">America/Santiago - Chile Time (CLT)</option>
              <option value="America/Punta_Arenas">America/Punta_Arenas - Chile Time (CLT)</option>
              <option value="America/Caracas">America/Caracas - Venezuela Time (VET)</option>
              <option value="America/La_Paz">America/La_Paz - Bolivia Time (BOT)</option>
              <option value="America/Lima">America/Lima - Peru Time (PET)</option>
              <option value="America/Bogota">America/Bogota - Colombia Time (COT)</option>
              <option value="America/Guayaquil">America/Guayaquil - Ecuador Time (ECT)</option>
              <option value="America/Georgetown">America/Georgetown - Guyana Time (GYT)</option>
              <option value="America/Paramaribo">America/Paramaribo - Suriname Time (SRT)</option>
              <option value="America/Cayenne">America/Cayenne - French Guiana Time (GFT)</option>
              <option value="America/Montevideo">America/Montevideo - Uruguay Time (UYT)</option>
              <option value="America/Asuncion">America/Asuncion - Paraguay Time (PYT)</option>
              <option value="America/Mexico_City">America/Mexico_City - Central Time (CT)</option>
              <option value="America/Cancun">America/Cancun - Eastern Time (ET)</option>
              <option value="America/Merida">America/Merida - Central Time (CT)</option>
              <option value="America/Monterrey">America/Monterrey - Central Time (CT)</option>
              <option value="America/Matamoros">America/Matamoros - Central Time (CT)</option>
              <option value="America/Mazatlan">America/Mazatlan - Mountain Time (MT)</option>
              <option value="America/Chihuahua">America/Chihuahua - Mountain Time (MT)</option>
              <option value="America/Ojinaga">America/Ojinaga - Mountain Time (MT)</option>
              <option value="America/Hermosillo">America/Hermosillo - Mountain Time (MT)</option>
              <option value="America/Tijuana">America/Tijuana - Pacific Time (PT)</option>
              <option value="America/Bahia_Banderas">America/Bahia_Banderas - Central Time (CT)</option>
              <option value="America/Toronto">America/Toronto - Eastern Time (ET)</option>
              <option value="America/Vancouver">America/Vancouver - Pacific Time (PT)</option>
              <option value="America/Edmonton">America/Edmonton - Mountain Time (MT)</option>
              <option value="America/Winnipeg">America/Winnipeg - Central Time (CT)</option>
              <option value="America/Regina">America/Regina - Central Time (CT)</option>
              <option value="America/Halifax">America/Halifax - Atlantic Time (AT)</option>
              <option value="America/St_Johns">America/St_Johns - Newfoundland Time (NST)</option>
              <option value="America/Glace_Bay">America/Glace_Bay - Atlantic Time (AT)</option>
              <option value="America/Moncton">America/Moncton - Atlantic Time (AT)</option>
              <option value="America/Goose_Bay">America/Goose_Bay - Atlantic Time (AT)</option>
              <option value="America/Blanc-Sablon">America/Blanc-Sablon - Atlantic Time (AT)</option>
              <option value="America/Montreal">America/Montreal - Eastern Time (ET)</option>
              <option value="America/Nipigon">America/Nipigon - Eastern Time (ET)</option>
              <option value="America/Thunder_Bay">America/Thunder_Bay - Eastern Time (ET)</option>
              <option value="America/Iqaluit">America/Iqaluit - Eastern Time (ET)</option>
              <option value="America/Pangnirtung">America/Pangnirtung - Eastern Time (ET)</option>
              <option value="America/Atikokan">America/Atikokan - Eastern Time (ET)</option>
              <option value="America/Wawa">America/Wawa - Eastern Time (ET)</option>
              <option value="America/Nipigon">America/Nipigon - Eastern Time (ET)</option>
              <option value="America/Rainy_River">America/Rainy_River - Central Time (CT)</option>
              <option value="America/Resolute">America/Resolute - Central Time (CT)</option>
              <option value="America/Rankin_Inlet">America/Rankin_Inlet - Central Time (CT)</option>
              <option value="America/Inuvik">America/Inuvik - Mountain Time (MT)</option>
              <option value="America/Yellowknife">America/Yellowknife - Mountain Time (MT)</option>
              <option value="America/Cambridge_Bay">America/Cambridge_Bay - Mountain Time (MT)</option>
              <option value="America/Creston">America/Creston - Mountain Time (MT)</option>
              <option value="America/Dawson_Creek">America/Dawson_Creek - Mountain Time (MT)</option>
              <option value="America/Fort_Nelson">America/Fort_Nelson - Mountain Time (MT)</option>
              <option value="America/Whitehorse">America/Whitehorse - Pacific Time (PT)</option>
              <option value="America/Dawson">America/Dawson - Pacific Time (PT)</option>
            </select>
          </div>
        
        </div>

    {/* Update Type Toggle */}
<div className="mb-8">
  <div className="flex items-center gap-4 mb-6">
    <span className="text-white font-medium">UPDATE TYPE</span>
    <div className="flex items-center">
      <div className="relative inline-block w-12 h-6 bg-gray-600 rounded-full">
        <div
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ease-in-out ${
            formData.updateType === 'LATEST_UPDATE' ? 'translate-x-3' : 'translate-x-0.5'
          }`}
        ></div>
      </div>
    </div>
    <div className="flex gap-4">
      <button
        type="button"
        className={`px-4 py-1 text-sm font-medium border ${
          formData.updateType === 'BREAKING_NEWS'
            ? 'bg-red-600 text-white border-red-600'
            : 'bg-transparent text-white border-white'
        }`}
        onClick={() => setFormData(prev => ({ ...prev, updateType: 'BREAKING_NEWS' }))}
      >
        BREAKING NEWS
      </button>
      <button
        type="button"
        className={`px-4 py-1 text-sm font-medium border ${
          formData.updateType === 'LATEST_UPDATE'
            ? 'bg-red-600 text-white border-red-600'
            : 'bg-transparent text-white border-white'
        }`}
        onClick={() => setFormData(prev => ({ ...prev, updateType: 'LATEST_UPDATE' }))}
      >
        LATEST UPDATE
      </button>
    </div>
  </div>
  
  {/* Status Visibility Toggle */}
  <div className=" gap-4 hidden">
    <span className="text-white font-medium">UPDATE TYPE VISIBILITY</span>
    <div className="flex items-center">
      <div className="relative inline-block w-12 h-6 bg-gray-600 rounded-full">
        <div
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ease-in-out ${
            formData.statusVisible ? 'translate-x-3' : 'translate-x-0.5'
          }`}
        ></div>
      </div>
    </div>
    <span className="text-white text-sm">
      {formData.statusVisible ? 'VISIBLE' : 'HIDDEN'}
    </span>
    <button
      type="button"
      onClick={() => setFormData(prev => ({ ...prev, statusVisible: !prev.statusVisible }))}
      className="text-white hover:text-gray-300 transition-colors"
    >
      Toggle
    </button>
  </div>
</div>

        {/* Headlines */}
        <div className="space-y-6">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Headline 1</label>
            <input
              type="text"
              name="headline1"
              value={formData.headline1}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">Sub Headline</label>
            <input
              type="text"
              name="subHeadline"
              value={formData.subHeadline}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">Headline 3</label>
            <input
              type="text"
              name="headline3"
              value={formData.headline3}
              onChange={handleChange}
              className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
              placeholder="Type Text Here"
            />
          </div>
        </div>

        {/* Flight Events */}
        <div className="mt-12">
          <h2 className="text-white text-xl font-medium mb-8">Flight Events</h2>
          
            <div>
              <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-medium">Events</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs font-medium border ${
                    flightOrientation === 'VERTICAL' 
                        ? 'bg-red-600 text-white border-red-600' 
                        : 'bg-transparent text-white border-white'
                    }`}
                  onClick={() => toggleEventOrientation('FLIGHT_EVENT', 'VERTICAL')}
                  >
                    VERTICAL
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs font-medium border ${
                    flightOrientation === 'HORIZONTAL' 
                        ? 'bg-red-600 text-white border-red-600' 
                        : 'bg-transparent text-white border-white'
                    }`}
                  onClick={() => toggleEventOrientation('FLIGHT_EVENT', 'HORIZONTAL')}
                  >
                    HORIZONTAL
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
              {flightEvents.map((event, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <input
                      type="text"
                      value={event.eventName}
                    onChange={(e) => handleEventChange('FLIGHT_EVENT', index, 'eventName', e.target.value)}
                      className="flex-1 bg-black border border-white text-white px-3 py-2 text-sm focus:outline-none focus:border-red-500"
                      placeholder="Type Countdown Event Here"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        disabled={event.timeOffset === -999999}
                        defaultValue={(() => {
                          if (event.timeOffset === -999999) return 'T-0';
                          const totalSeconds = Math.abs(event.timeOffset);
                          const minutes = Math.floor(totalSeconds / 60);
                          const seconds = totalSeconds % 60;
                          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        })()}
                        onInput={(e) => {
                          if (event.timeOffset === -999999) return;
                          const value = e.target.value;
                          
                          // Allow only digits and colon
                          const cleanValue = value.replace(/[^\d:]/g, '');
                          
                          // Prevent more than one colon
                          const colonCount = (cleanValue.match(/:/g) || []).length;
                          if (colonCount > 1) {
                            e.target.value = cleanValue.replace(/:/g, '').replace(/(\d+)/, '$1:');
                            return;
                          }
                          
                          // If there's a colon, limit seconds to 2 digits
                          if (cleanValue.includes(':')) {
                            const parts = cleanValue.split(':');
                            if (parts.length === 2) {
                              const minutes = parts[0];
                              const seconds = parts[1];
                              
                              // Limit seconds to 2 digits
                              if (seconds.length > 2) {
                                e.target.value = `${minutes}:${seconds.substring(0, 2)}`;
                                return;
                              }
                              
                              // Prevent seconds > 59
                              if (seconds.length === 2 && parseInt(seconds) > 59) {
                                e.target.value = `${minutes}:59`;
                                return;
                              }
                            }
                          }
                          
                          e.target.value = cleanValue;
                        }}
                        onBlur={(e) => {
                          if (event.timeOffset === -999999) return;
                          const timeString = e.target.value;
                          
                          // Allow empty input
                          if (timeString === '') {
                          handleEventChange('FLIGHT_EVENT', index, 'timeOffset', 0);
                            return;
                          }
                          
                          // Check for valid format: M:SS or MM:SS
                          const timeRegex = /^(\d+):(\d{1,2})$/;
                          const match = timeString.match(timeRegex);
                          
                          if (match) {
                            const minutes = parseInt(match[1]);
                            const seconds = parseInt(match[2]);
                            // Validate seconds are 0-59 (2 digits max)
                            if (seconds >= 0 && seconds < 60) {
                              const totalSeconds = minutes * 60 + seconds;
                              const currentOffset = event.timeOffset === -999999 ? 0 : event.timeOffset;
                              const newOffset = currentOffset < 0 ? -totalSeconds : totalSeconds;
                            handleEventChange('FLIGHT_EVENT', index, 'timeOffset', newOffset);
                            } else {
                              // Invalid seconds, reset to current value
                              const totalSeconds = event.timeOffset === -999999 ? 0 : Math.abs(event.timeOffset || 0);
                              const minutes = Math.floor(totalSeconds / 60);
                              const seconds = totalSeconds % 60;
                              e.target.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                            }
                          } else {
                            // If invalid format, reset to current value
                            const totalSeconds = event.timeOffset === -999999 ? 0 : Math.abs(event.timeOffset || 0);
                            const minutes = Math.floor(totalSeconds / 60);
                            const seconds = totalSeconds % 60;
                            e.target.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                          }
                        }}
                        className="w-20 bg-black border border-white text-white px-2 py-2 text-sm text-center focus:outline-none focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder={event.timeOffset === -999999 ? "T-0" : "M:SS"}
                      />
                      <select
                        value={event.timeOffset === -999999 ? 't0' : (event.timeOffset < 0 ? 'before' : 'after')}
                        onChange={(e) => {
                          if (e.target.value === 't0') {
                            handleEventChange('FLIGHT_EVENT', index, 'timeOffset', -999999);
                          } else {
                            const isBefore = e.target.value === 'before';
                            // If currently T-0, use 0 as base, otherwise use current offset
                            const absOffset = event.timeOffset === -999999 ? 0 : Math.abs(event.timeOffset || 0);
                            const newOffset = isBefore ? -absOffset : absOffset;
                            handleEventChange('FLIGHT_EVENT', index, 'timeOffset', newOffset);
                          }
                        }}
                        className="bg-black border border-white text-white px-2 py-2 text-sm focus:outline-none focus:border-red-500"
                      >
                        <option value="before">T-</option>
                        <option value="t0">T-0</option>
                        <option value="after">T+</option>
                      </select>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" className="text-white hover:text-gray-300">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </button>
                      <button 
                        type="button" 
                      onClick={() => removeEvent('FLIGHT_EVENT', index)}
                        className="text-white hover:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Add placeholder events if needed to show the visual structure */}
              {flightEvents.length === 0 && (
                  <div className="flex items-center gap-3 opacity-50">
                    <input
                      type="text"
                      className="flex-1 bg-black border border-white text-gray-500 px-3 py-2 text-sm"
                    placeholder="Click 'Add Flight Event' to add events"
                      readOnly
                    />
                    <select
                      disabled
                      className="bg-black border border-white text-gray-500 px-3 py-2 text-sm"
                    >
                      <option>Select Time +/- T-0</option>
                    </select>
                    <div className="flex gap-1">
                      <button type="button" className="text-gray-500 cursor-not-allowed">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </button>
                      <button type="button" className="text-gray-500 cursor-not-allowed">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Add Event Button */}
        <div className="mt-4">
            <button
              type="button"
            onClick={() => addEvent('FLIGHT_EVENT')}
              className="w-full bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
            + Add Flight Event
            </button>
        </div>

        {/* Save Button */}
        <div className="flex justify-center pt-8">
          <button
            type="submit"
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-8 py-3 font-medium transition-colors"
          >
            {saving ? 'Saving...' : (isEdit ? 'UPDATE OVERLAY' : 'CREATE OVERLAY')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default OverlayEditor